/**
 * Scroll-linked reveal using the nearest `.section-wrapper` (or `.shopify-section`) as context.
 *
 * Attributes:
 * - direction: left | right | top | bottom
 * - distance: px when full-exit is off (default 48)
 * - speed: 1–10 (reserved; entrance no longer uses scroll-distance boost — reveal follows visibility)
 * - exit-percent: 0–100, viewport height % — exit starts after section top passes this far above the viewport top (default 30)
 * - exit-span-percent: 0–100 — after exit starts, distance over which motion completes (default 35)
 * - full-exit: "true" for left/right — travel distance at least enough to leave the viewport horizontally
 * - intro-first: "true" — play one-time intro from top/bottom on load (see intro-from)
 * - intro-from: top | bottom — only with intro-first
 */

/** @param {number} t @returns {number} */
function smoothstep(t) {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

/** Subpixel / layout tolerance so “at rest” clears transforms. */
const VIEW_PAD = 2;

/** Fraction of the element’s height that must be in the viewport before reveal begins (~IntersectionObserver 0.1). */
const ENTER_VISIBLE_THRESHOLD = 0.1;

/**
 * @param {DOMRectReadOnly} rect
 * @param {number} vh
 * @returns {number} visible height / rect.height
 */
function verticalOverlapRatio(rect, vh) {
  if (rect.height <= 0) return 0;
  const visible = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
  return Math.min(1, visible / rect.height);
}

class TextScrollReveal extends HTMLElement {
  /** @type {number | null} */
  #raf = null;

  /** @type {boolean} */
  #introDone = true;

  /** @type {() => void} */
  #onScroll = () => this.#schedule();

  static get observedAttributes() {
    return [
      'direction',
      'distance',
      'speed',
      'exit-percent',
      'exit-span-percent',
      'full-exit',
      'intro-first',
      'intro-from',
    ];
  }

  attributeChangedCallback() {
    this.#schedule();
  }

  connectedCallback() {
    const introFirst = this.getAttribute('intro-first') === 'true';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (introFirst && !reduced) {
      this.#introDone = false;
      queueMicrotask(() => this.#runIntro());
    }

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#schedule();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    if (this.#raf != null) cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #schedule() {
    if (this.#raf != null) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = null;
      this.#apply();
    });
  }

  #runIntro() {
    const target = this.firstElementChild;
    if (!(target instanceof HTMLElement)) {
      this.#introDone = true;
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#introDone = true;
      return;
    }

    const edge = (this.getAttribute('intro-from') ?? 'bottom').toLowerCase();
    const fromBottom = edge !== 'top';
    const startY = fromBottom ? Math.min(80, window.innerHeight * 0.08) : -Math.min(80, window.innerHeight * 0.08);
    const duration = 820;
    const start = performance.now();

    target.style.opacity = '0';

    /** @param {number} now */
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 2.4;
      const y = startY * (1 - eased);
      target.style.transform = `translate3d(0, ${y}px, 0)`;
      target.style.opacity = String(smoothstep(eased));

      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        target.style.transform = '';
        this.#introDone = true;
        requestAnimationFrame(() => this.#apply());
      }
    };

    target.style.transform = `translate3d(0, ${startY}px, 0)`;
    requestAnimationFrame(step);
  }

  /** @returns {HTMLElement | null} */
  #getScrollRoot() {
    const fromClosest = this.closest('.section-wrapper') ?? this.closest('.shopify-section');
    if (fromClosest instanceof HTMLElement) return fromClosest;
    return this.parentElement;
  }

  #apply() {
    const target = this.firstElementChild;
    if (!(target instanceof HTMLElement)) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      target.style.transform = '';
      target.style.opacity = '';
      return;
    }

    if (!this.#introDone) {
      return;
    }

    const root = this.#getScrollRoot();
    if (!root) return;

    const rr = root.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    const exitPercent = Math.min(100, Math.max(0, parseFloat(this.getAttribute('exit-percent') ?? '30'))) / 100;
    const exitSpanPercent = Math.min(100, Math.max(5, parseFloat(this.getAttribute('exit-span-percent') ?? '35'))) / 100;

    const exitStartPx = exitPercent * vh;
    const exitSpanPx = exitSpanPercent * vh;

    /** Measure the visible text block — not the host wrapper (flex/grid can skew host bounds). */
    const tr = target.getBoundingClientRect();
    let elementOverlap = verticalOverlapRatio(tr, vh);

    /**
     * Entrance: driven by how much of the element is in view, not scroll-distance ramp.
     * Hidden until at least ENTER_VISIBLE_THRESHOLD of its height is visible; then map
     * linearly to fully revealed as overlap goes from threshold → 100%.
     */
    let revealProgress = 0;
    if (elementOverlap > ENTER_VISIBLE_THRESHOLD) {
      revealProgress = Math.min(
        1,
        (elementOverlap - ENTER_VISIBLE_THRESHOLD) / (1 - ENTER_VISIBLE_THRESHOLD),
      );
    }

    /**
     * At rest: no residual translate. Snap to fully revealed when the copy is
     * entirely on-screen, or nearly all of its height is visible (rounding).
     */
    const fitsInViewport = tr.height <= vh + VIEW_PAD * 2;
    const whollyInside =
      fitsInViewport &&
      tr.top >= -VIEW_PAD &&
      tr.bottom <= vh + VIEW_PAD;
    if (whollyInside || elementOverlap >= 0.99) {
      revealProgress = 1;
    }

    let progress = revealProgress;

    if (rr.bottom <= 0 || rr.top >= vh) {
      progress = 0;
    } else if (rr.top < -exitStartPx) {
      const extra = -rr.top - exitStartPx;
      const exitFactor = Math.max(0, 1 - Math.min(1, extra / Math.max(1, exitSpanPx)));
      progress *= exitFactor;
    }

    let easedProgress = smoothstep(progress);

    /** Kill tiny offsets from float math + large full-exit distances. */
    if (easedProgress > 0.995) {
      easedProgress = 1;
    }

    const dir = (this.getAttribute('direction') ?? 'left').toLowerCase();
    const fullExit = this.getAttribute('full-exit') === 'true';
    let distancePx = parseFloat(this.getAttribute('distance') ?? '48');
    if (!Number.isFinite(distancePx)) distancePx = 48;

    if (fullExit && (dir === 'left' || dir === 'right')) {
      if (dir === 'left') {
        distancePx = Math.max(distancePx, tr.right + 16);
      } else {
        distancePx = Math.max(distancePx, vw - tr.left + 16);
      }
    }

    const off = distancePx * (1 - easedProgress);

    let tx = 0;
    let ty = 0;
    if (dir === 'left') tx = -off;
    else if (dir === 'right') tx = off;
    else if (dir === 'top') ty = -off;
    else if (dir === 'bottom') ty = off;

    if (Math.abs(tx) < 0.5 && Math.abs(ty) < 0.5) {
      tx = ty = 0;
    }

    target.style.transform =
      tx === 0 && ty === 0 ? '' : `translate3d(${tx}px, ${ty}px, 0)`;
    target.style.opacity = String(easedProgress);
  }
}

if (!customElements.get('text-scroll-reveal')) {
  customElements.define('text-scroll-reveal', TextScrollReveal);
}
